import React, { useMemo, useState } from 'react';
import { Box, Typography, Grid, Card, CardContent, CardActions, Button, Alert, Checkbox, Stack } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import { useSchool } from '../contexts/SchoolContext';
import Spinner from '../components/Spinner';

const SchoolReportCardClass: React.FC = () => {
  const navigate = useNavigate();
  const { className } = useParams<{ className: string }>();
  const { students, loading, publishStudentDocuments } = useSchool();
  const [selectedStudentIds, setSelectedStudentIds] = useState<string[]>([]);
  const [publishing, setPublishing] = useState(false);
  const [publishMessage, setPublishMessage] = useState<string | null>(null);
  const [publishError, setPublishError] = useState<string | null>(null);
  const decodedClassName = decodeURIComponent(className || '');

  const studentsInClass = useMemo(() => (
    students.filter((student) => (student.className?.trim() || 'Unassigned') === decodedClassName)
  ), [students, decodedClassName]);

  const toggleStudentSelection = (studentId: string) => {
    setSelectedStudentIds((prev) => prev.includes(studentId)
      ? prev.filter((id) => id !== studentId)
      : [...prev, studentId]
    );
  };

  const handlePublish = async (publishType: 'reportCard' | 'examTicket', allExcept = false, useSelection = false) => {
    setPublishMessage(null);
    setPublishError(null);
    setPublishing(true);
    try {
      const studentIds = useSelection ? selectedStudentIds : [];
      await publishStudentDocuments(decodedClassName, publishType, studentIds, allExcept);
      setSelectedStudentIds([]);
      setPublishMessage(allExcept
        ? 'All students in this class except selected students are now published.'
        : useSelection
          ? 'Selected students are now published.'
          : 'All students in this class are now published.'
      );
    } catch (error: any) {
      setPublishError(error?.response?.data?.message || 'Failed to publish report cards. Please try again.');
    } finally {
      setPublishing(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" mt={4}>
        <Spinner />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 600 }}>
            {decodedClassName} Report Cards
          </Typography>
          <Typography color="text.secondary" sx={{ mt: 1 }}>
            Select a student to preview and print their report card.
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button variant="outlined" onClick={() => navigate('/school/report-cards')}>
            Back to Classes
          </Button>
          <Button variant="outlined" onClick={() => navigate('/school/dashboard')}>
            Back to Dashboard
          </Button>
        </Box>
      </Box>

      <Box sx={{ mb: 3 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} flexWrap="wrap" sx={{ mb: 2 }}>
          <Button
            variant="contained"
            disabled={publishing || studentsInClass.length === 0}
            onClick={() => handlePublish('reportCard', false, false)}
          >
            Publish All
          </Button>
          <Button
            variant="outlined"
            disabled={publishing || selectedStudentIds.length === 0}
            onClick={() => handlePublish('reportCard', false, true)}
          >
            Publish Selected Students
          </Button>
          <Button
            variant="outlined"
            disabled={publishing || studentsInClass.length === 0}
            onClick={() => handlePublish('reportCard', true, true)}
          >
            Publish All Except Selected
          </Button>
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {selectedStudentIds.length > 0 ? `${selectedStudentIds.length} student(s) selected.` : 'Select students to publish individually or publish all students in this class.'}
        </Typography>
        {publishMessage && <Alert severity="success" sx={{ mb: 2 }}>{publishMessage}</Alert>}
        {publishError && <Alert severity="error" sx={{ mb: 2 }}>{publishError}</Alert>}
      </Box>

      {studentsInClass.length === 0 ? (
        <Alert severity="info">No students were found for the selected class.</Alert>
      ) : (
        <Grid container spacing={3}>
          {studentsInClass.map((student) => (
            <Grid item xs={12} sm={6} md={4} key={student._id}>
              <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <CardContent sx={{ flexGrow: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Checkbox
                      checked={selectedStudentIds.includes(student._id)}
                      onChange={() => toggleStudentSelection(student._id)}
                      inputProps={{ 'aria-label': `Select ${student.name}` }}
                    />
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      {student.name}
                    </Typography>
                  </Box>
                  <Typography color="text.secondary">Registration ID: {student.registrationId || 'N/A'}</Typography>
                  <Typography color="text.secondary">Email: {student.email}</Typography>
                  <Typography sx={{ mt: 1, color: student.reportCardPublished ? 'success.main' : 'text.secondary' }}>
                    {student.reportCardPublished ? 'Published' : 'Not yet published'}
                  </Typography>
                </CardContent>
                <CardActions>
                  <Button size="small" onClick={() => navigate(`/school/report-cards/student/${student._id}`)}>
                    Preview Report Card
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
    </Box>
  );
};

export default SchoolReportCardClass;
